import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import {
    SharedConsumerLoadRelatedListAction,
    SharedConsumerSetConsumerMovementFiltersAction,
    SharedConsumerSetConsumerSalesFiltersAction
} from 'src/app/_shared/stores/shared-consumer/shared-consumer.action';
import { KalDate } from '@kalidea/kaligraphi';
import {
    ActiviteDto,
    CeDelegIsGlobaleModel,
    ChampListeValeurDto,
    DelegationBaseModel,
    EnveloppeQuotaDto,
    FamilleProduitDto,
    MutationGetListeActiviteGeneriqueArgs,
    MutationGetListeChampGeneriqueArgs,
    MutationGetListeDelegationIsGlobaleModelArgs,
    MutationGetListeEnveloppeGeneriqueArgs,
    MutationGetListeFamilleProduitGeneriqueArgs,
    MutationGetListeSiteGeneriqueAPlatArgs,
    MutationGetListeTypeConsoMouvementArgs,
    SiteDto,
    TypeConsoMouvementPointDto
} from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { UserState } from 'src/app/_user/store/user/user.state';
import { forkJoin, Observable, of } from 'rxjs';

export interface SharedConsumerMovementFilters {
    filterBy: string;
    IdDelegation?: number;
    IdEnveloppe?: number;
    IdBeneficiaire?: number;
    IdTypeContrat?: number;
    IdSite?: number;
    displayOption?: string;
    movementOrigin?: string;
    orderDatepicker?: { startDate: KalDate, endDate: KalDate };
    movement?: string;
    seeSelect?: string;
}

export interface SharedConsumerSalesFilters {
    filterBy?: SharedConsumerSalesTypesFilters;
    WithBenefHorsDroits?: boolean;
    IdEnveloppe?: number;
    IdBenef?: number;
    IdTypeContrat?: number;
    IdSite?: number;
    IdActivite?: number;
    IdFamille?: number;
    TypeConsommation?: string;
}

export enum SharedConsumerSalesTypesFilters {
    IsTransverse,
    IsEnveloppeForAttribution,
    IsActiviteFamille,
    IsBeneficiaire
}

export class SharedConsumerModel {

    consumerMovementFilters: SharedConsumerMovementFilters = null;

    consumerSalesFilters: SharedConsumerSalesFilters = null;

    envelopeLabel: string = null;

    sitesList: SiteDto[] = [];

    contractsList: ChampListeValeurDto[] = [];

    interactivitesEnvelopesList: EnveloppeQuotaDto[] = [];

    allocationsEnvelopesList: EnveloppeQuotaDto[] = [];

    activitesList: ActiviteDto[] = [];

    familyProductsList: FamilleProduitDto[] = [];

    delegationsList: (CeDelegIsGlobaleModel | DelegationBaseModel)[] = [];

    movementConsumptionsList: TypeConsoMouvementPointDto[] = [];

}

@Injectable()
@State<SharedConsumerModel>({
    name: 'subventionConsumerMovement',
    defaults: new SharedConsumerModel()
})
export class SharedConsumerState {

    constructor(private readonly graphqlService: GraphqlService,
                private readonly store: Store) {
    }

    private get globalDelegationId(): number {
        const delegationId = this.store.selectSnapshot(UserState.currentDelegation)?.Id;
        const isGlobalDelegation = this.store.selectSnapshot(UserState.isCurrentDelegationGlobal);

        return !isGlobalDelegation ? delegationId : null;
    }

    @Selector()
    static sitesList(state: SharedConsumerModel): SiteDto[] {
        return state.sitesList;
    }

    @Selector()
    static contractsList(state: SharedConsumerModel): ChampListeValeurDto[] {
        return state.contractsList;
    }

    @Selector()
    static consumerMovementFilters(state: SharedConsumerModel): SharedConsumerMovementFilters {
        return state.consumerMovementFilters;
    }

    @Selector()
    static consumerSalesFilters(state: SharedConsumerModel): SharedConsumerSalesFilters {
        return state.consumerSalesFilters;
    }

    @Selector()
    static envelopeLabel(state: SharedConsumerModel): string {
        return state.envelopeLabel;
    }

    @Selector()
    static interactivitesEnvelopesList(state: SharedConsumerModel): EnveloppeQuotaDto[] {
        return state.interactivitesEnvelopesList;
    }

    @Selector()
    static allocationsEnvelopesList(state: SharedConsumerModel): EnveloppeQuotaDto[] {
        return state.allocationsEnvelopesList;
    }

    @Selector()
    static activitesList(state: SharedConsumerModel): ActiviteDto[] {
        return state.activitesList;
    }

    @Selector()
    static familyProductsList(state: SharedConsumerModel): FamilleProduitDto[] {
        return state.familyProductsList;
    }

    @Selector()
    static movementConsumptionsList(state: SharedConsumerModel): TypeConsoMouvementPointDto[] {
        return state.movementConsumptionsList;
    }

    @Selector()
    static delegationsList(state: SharedConsumerModel): (CeDelegIsGlobaleModel | DelegationBaseModel)[] {
        return state.delegationsList;
    }

    @Action(SharedConsumerSetConsumerMovementFiltersAction)
    setConsumerMovementFilters({patchState}: StateContext<SharedConsumerModel>, {params}: SharedConsumerSetConsumerMovementFiltersAction) {
        patchState({
            consumerMovementFilters: params?.filters || null,
            envelopeLabel: params?.envelopeLabel || null
        });
    }

    @Action(SharedConsumerSetConsumerSalesFiltersAction)
    setConsumerSalesFilters({patchState}: StateContext<SharedConsumerModel>, {filters}: SharedConsumerSetConsumerSalesFiltersAction) {
        patchState({
            consumerSalesFilters: filters || null,
        });
    }

    @Action(SharedConsumerLoadRelatedListAction)
    loadRelatedList(state: StateContext<SharedConsumerModel>) {
        return forkJoin([
            this.getListeSiteGeneriqueAPlat(state),
            this.getContractsList(state),
            this.getActivitiesList(state),
            this.getFamilyProductsList(state),
            this.getMovementConsumptionsList(state),
            this.getListeEnveloppeGenerique({isAllocation: true}).pipe(tap(e => state.patchState({allocationsEnvelopesList: e}))),
            this.getListeEnveloppeGenerique({isAllocation: false}).pipe(tap(e => state.patchState({interactivitesEnvelopesList: e}))),
        ]).pipe(
            take(1),
            switchMap(() => {
                return this.store.selectSnapshot(UserState.isCurrentDelegationGlobal) ? this.getDelegationsList(state) : of(null);
            }),
        );
    }

    private getMovementConsumptionsList({patchState}: StateContext<SharedConsumerModel>): Observable<any> {
        return this.graphqlService.mutateOne<'GetListeTypeConsoMouvement', MutationGetListeTypeConsoMouvementArgs>({
            mutation: gql`
                mutation ConsumptionTypesList($contexte: ContextInfosInput, $criteres: CriteresTypeConsoMouvementPointDTOInput) {
                  GetListeTypeConsoMouvement(
                    contexte: $contexte
                    criteres: $criteres
                  ) {
                    TypeConsoMouvementPointDTO {
                      Id
                      Code
                    }
                  }
                }
              `
        }).pipe(
            tap(GetListeTypeConsoMouvement => {
                patchState({
                    movementConsumptionsList: GetListeTypeConsoMouvement?.TypeConsoMouvementPointDTO || []
                });
            })
        );
    }

    private getDelegationsList({patchState}: StateContext<SharedConsumerModel>): Observable<any> {
        return this.graphqlService.mutateOne<'GetListeDelegationIsGlobaleModel', MutationGetListeDelegationIsGlobaleModelArgs>({
            mutation: gql`
                mutation GetListeDelegationIsGlobaleModel($contexte: ContextInfosInput, $criteres: CriteresDelegationModelDTOInput) {
                  GetListeDelegationIsGlobaleModel(
                    contexte: $contexte
                    criteres: $criteres
                  ) {
                    CEDelegIsGlobaleModel{
                      ...on CEDelegIsGlobaleModel {
                        Id
                        Nom
                        IsGlobale
                      }
                    }
                  }
                }
              `, variables: {
                criteres: {
                    IsClos: false
                }
            }
        }).pipe(
            tap(GetListeDelegationIsGlobaleModel => {
                patchState({
                    delegationsList: GetListeDelegationIsGlobaleModel?.CEDelegIsGlobaleModel || []
                });
            })
        );
    }

    private getListeEnveloppeGenerique({isAllocation} = {isAllocation: false}): Observable<any> {
        return this.graphqlService.mutateOne<'GetListeEnveloppeGenerique', MutationGetListeEnveloppeGeneriqueArgs>({
            mutation: gql`
                mutation GetListeEnveloppeGenerique($contexte: ContextInfosInput, $critere: CriteresEnveloppeQuotaDTOInput) {
                  GetListeEnveloppeGenerique(
                    contexte: $contexte
                    critere: $critere
                  ) {
                    EnveloppeQuotaDTO {
                      Id
                      Libelle
                      ListeMoteurRegle {
                        MoteurRegleDTO {
                          Id
                          IsCloture
                        }
                      }
                    }
                  }
                }
              `, variables: {
                critere: {
                    IsCloture: false,
                    IdCECurrentOuCEGlobal: this.globalDelegationId,
                    IsTransverse: !isAllocation,
                    IsEnveloppeAttrib: isAllocation,
                    TakeMoteurRegle: true
                }
            }
        }).pipe(
            map(GetListeEnveloppeGenerique => GetListeEnveloppeGenerique?.EnveloppeQuotaDTO || [])
        );
    }

    private getListeSiteGeneriqueAPlat({patchState}: StateContext<SharedConsumerModel>): Observable<any> {
        return this.graphqlService.mutateOne<'GetListeSiteGeneriqueAPlat', MutationGetListeSiteGeneriqueAPlatArgs>({
            mutation: gql`
              mutation GetListeSiteGeneriqueAPlat($contexte: ContextInfosInput, $criteres: CriteresSiteDTOInput) {
                  GetListeSiteGeneriqueAPlat(
                    contexte: $contexte
                    criteres: $criteres
                    ){
                       SiteDTO {
                         Id
                         Nom
                         SiteParent {
                           Id
                         }
                       }
                    }
              }
              `, variables: {
                criteres: {
                    IsCloture: false
                }
            }
        }).pipe(
            tap(GetListeSiteGeneriqueAPlat => {
                patchState({
                    sitesList: GetListeSiteGeneriqueAPlat?.SiteDTO || []
                });
            })
        );
    }

    private getActivitiesList({patchState}: StateContext<SharedConsumerModel>): Observable<any> {
        return this.graphqlService.mutateOne<'GetListeActiviteGenerique', MutationGetListeActiviteGeneriqueArgs>({
            mutation: gql`
                mutation GetListeActiviteGenerique($contexte: ContextInfosInput, $criteres: CriteresActiviteDTOInput) {
                    GetListeActiviteGenerique(
                        contexte: $contexte
                        criteres: $criteres
                    ){
                        ActiviteDTO {
                            Id
                            Libelle
                            Enveloppe {
                                Id
                            }
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    IsCloture: false,
                    IdCEOuCEGlobalProprietaire: this.globalDelegationId,
                    WithEnveloppe: true
                }
            }
        }).pipe(
            tap(GetListeActiviteGenerique => {
                patchState({
                    activitesList: GetListeActiviteGenerique?.ActiviteDTO || []
                });
            })
        );
    }

    private getContractsList({patchState}: StateContext<SharedConsumerModel>): Observable<any> {
        return this.graphqlService.mutateOne<'GetListeChampGenerique', MutationGetListeChampGeneriqueArgs>({
            mutation: gql`
                mutation GetListeChampGenerique($contexte: ContextInfosInput, $critere: CriteresChampDTOInput) {
                  GetListeChampGenerique(
                    contexte: $contexte
                    critere: $critere
                  ) {
                    ChampDTO {
                      ListChampListeValeur {
                        ChampListeValeurDTO{
                          Id
                          Valeur
                          IsCloture
                        }
                      }
                    }
                  }
                }
              `, variables: {
                critere: {
                    Code: 'CTRAT',
                    TakeChampListeValeur: true
                }
            }
        }).pipe(
            tap(GetListeChampGenerique => {
                patchState({contractsList: GetListeChampGenerique?.ChampDTO?.[0]?.ListChampListeValeur?.ChampListeValeurDTO || []});
            })
        );
    }

    private getFamilyProductsList({patchState}: StateContext<SharedConsumerModel>): Observable<any> {
        return this.graphqlService.mutateOne<'GetListeFamilleProduitGenerique', MutationGetListeFamilleProduitGeneriqueArgs>({
            mutation: gql`
                mutation GetListeFamilleProduitGenerique($contexte: ContextInfosInput, $critere: CriteresFamilleProduitDTOInput) {
                  GetListeFamilleProduitGenerique(
                    contexte: $contexte
                    critere: $critere
                  ){
                  FamilleProduitDTO {
                    Id
                    Libelle
                        Activite {
                            Id
                            Code
                            Libelle
                        }
                        Enveloppe {
                            Id
                        }
                    }
                  }
                }
        `, variables: {
                critere: {
                    IsClos: false,
                    WithEnveloppe: true,
                    TakeActivite: true,
                    IdDelegationOuDelegationGlobale: this.globalDelegationId
                }
            }
        }).pipe(
            tap(GetListeFamilleProduitGenerique => {
                patchState({
                    familyProductsList: GetListeFamilleProduitGenerique?.FamilleProduitDTO || []
                });
            })
        );
    }

}
